import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import HeldenCampus from "@/components/site/HeldenCampus";
import HeldenSchatzkiste from "@/components/site/HeldenSchatzkiste";
import CampusMarketplace from "@/components/site/CampusMarketplace";
import CampusPricingTeaser from "@/components/site/CampusPricingTeaser";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CampusPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "pdl" });
  const [state, setState] = useState({ loading: false, success: false });

  useEffect(() => {
    document.title = "Dickies-Campus · Deutschlands stärkste Pflege-Community | Dickies-Campus";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement("meta"); meta.name = "description"; document.head.appendChild(meta); }
    meta.content = "Geschützte Community für PDLs, Praxisanleiter und Pflegekräfte. MDK-Soforthilfe, 100+ Mustervorlagen, monatliche Live-Briefings. Inklusive bei jeder Weiterbildung.";
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setState({ loading: true, success: false });
    try {
      await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        phone: form.phone || "noch nicht angegeben",
        audience: form.role === "traeger" ? "pflegedienst" : "pflegekraft",
        course_interest: form.role === "traeger" ? "Dickies-Campus – Unternehmens-Beratung" : "Dickies-Campus – Beratungsanfrage",
        message: "",
        consent: true,
        source: "campus-page",
      });
      setState({ loading: false, success: true });
      setForm({ name: "", email: "", phone: "", role: form.role });
    } catch {
      setState({ loading: false, success: false });
    }
  };

  return (
    <div className="min-h-screen bg-cream-50 text-sand-800">
      <Header />

      <main>
        {/* HERO */}
        <section className="pt-40 lg:pt-48 pb-20 lg:pb-24 bg-cream-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-3 mb-7">
              <span className="h-px w-10 bg-terra" />
              <span className="text-[11px] uppercase tracking-[0.3em] font-bold text-terra">
                Dickies-Campus · Exklusiv für Mitglieder
              </span>
              <span className="h-px w-10 bg-terra" />
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-[4.5rem] font-normal tracking-tight leading-[1.02] text-sand-800">
              Die Community, die <br />
              <span className="text-terra">deinen Rücken stärkt.</span>
            </h1>
            <p className="mt-7 font-display italic text-xl sm:text-2xl text-clay-dark leading-snug max-w-3xl mx-auto">
              Geschützt. Geprüft. Gegenseitig.
            </p>
            <p className="mt-6 text-sand-700 text-[15px] sm:text-base leading-[1.7] max-w-2xl mx-auto">
              Der Dickies-Campus ist die geschützte Mitgliedschaft für Pflegekräfte und Einrichtungen:
              MDK-Soforthilfe, geprüfte Doku-Vorlagen, monatliche Live-Briefings und ein
              Pflege-Marktplatz ohne Headhunter — alles unter einem Login.
            </p>

            {/* Pflege-KI Killer-Verheißung — der eine Satz, der hängenbleibt */}
            <Link
              to="#pflege-ki-demo"
              data-testid="campus-hero-pki-promise"
              className="mt-8 inline-flex items-center gap-3 rounded-full bg-espresso text-cream-50 pl-2 pr-5 h-12 text-[13.5px] font-semibold hover:bg-espresso-card transition-colors group"
            >
              <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-rose text-brand-darkred px-2.5 h-8 text-[10.5px] uppercase tracking-[0.22em] font-bold">
                <Sparkles className="h-3 w-3" strokeWidth={2.4} />
                Neu
              </span>
              <span>
                Pflege-KI: dein Diktat&nbsp;&rarr;&nbsp;fertiger Bericht in <span className="text-brand-rose font-bold">26&nbsp;Sek.</span>
              </span>
              <ArrowRight className="h-4 w-4 text-brand-rose group-hover:translate-x-1 transition-transform" strokeWidth={2} />
            </Link>

            {/* 3-Schritte-Erklärung – beseitigt Verwirrung über die Eintritts-Wege */}
            <div className="mt-12 max-w-3xl mx-auto grid sm:grid-cols-3 gap-4 sm:gap-3">
              {[
                { n: 1, t: "Paket wählen", d: "Pflege-KI Kompakt, Team oder Weiterbildung — monatlich kündbar" },
                { n: 2, t: "Zugang erhalten", d: "Login-Daten direkt nach der Buchung" },
                { n: 3, t: "Loslegen", d: "Patientenmappe, Vorlagen & Live-Briefings sofort nutzen" },
              ].map((s) => (
                <div key={s.n} className="text-left rounded-2xl bg-white border border-cream-300 p-5">
                  <div className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-terra text-cream-50 text-[12px] font-bold">
                    {s.n}
                  </div>
                  <div className="mt-3 text-[13.5px] font-semibold text-sand-800">{s.t}</div>
                  <div className="mt-1 text-[12.5px] text-sand-600 leading-snug">{s.d}</div>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap gap-3 justify-center">
              <Button asChild data-testid="campus-hero-prices" className="rounded-full bg-terra hover:bg-terra-dark text-white h-14 px-7 text-base font-semibold">
                <Link to="/preise">Preise &amp; Pakete ansehen</Link>
              </Button>
              <Button asChild variant="outline"
                data-testid="campus-hero-beratung"
                className="rounded-full h-14 px-7 text-base font-medium border border-sand-800 text-sand-800 hover:bg-white bg-transparent">
                <a href="#anfrage">Beratung anfragen</a>
              </Button>
            </div>
            <p className="mt-5 text-[12.5px] text-sand-500">
              Schon Mitglied?{" "}
              <a href="/campus/login" className="text-sand-800 underline-offset-4 hover:underline font-medium">
                Hier anmelden
              </a>
            </p>
          </div>
        </section>

        {/* The 3-Säulen-Block – Reuse */}
        <HeldenCampus compact />

        {/* Helden-Schatzkiste – konkrete Knaller-Dokumente */}
        <HeldenSchatzkiste />

        {/* Pflege-Marktplatz – das oft übersehene Versprechen */}
        <CampusMarketplace />

        {/* Pricing – schlanker Teaser, volle Preisseite unter /preise */}
        <CampusPricingTeaser />

        {/* Beratung & Kontakt */}
        <section id="anfrage" className="bg-cream-50 py-28 md:py-40">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-6">
                <span className="h-px w-10 bg-terra" />
                <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-terra">
                  Beratung &amp; Kontakt
                </span>
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.75rem] text-sand-800 font-normal tracking-tight leading-[1.1]">
                Nicht sicher, <span className="text-terra">welches Paket passt?</span>
              </h2>
              <p className="mt-6 text-sand-700 text-[15px] leading-relaxed max-w-xl">
                Sag uns kurz, wer du bist — wir melden uns persönlich und finden gemeinsam das
                richtige Paket für dich oder dein Team. Unverbindlich und ohne Verkaufsdruck.
              </p>
              <ul className="mt-7 space-y-3 max-w-xl">
                {[
                  "Persönliche Rückmeldung innerhalb von 48 Stunden",
                  "Ehrliche Empfehlung — auch wenn es das kleinere Paket ist",
                  "Live-Demo der Pflege-KI auf Wunsch",
                  "Konditionen für Träger & größere Teams",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-3 text-[15px] text-sand-800">
                    <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-terra/15 text-terra shrink-0">
                      <CheckCircle2 className="h-3 w-3" strokeWidth={2.6} />
                    </span>
                    <span className="font-medium">{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-3xl bg-white border border-cream-300 shadow-soft p-7 md:p-9">
                {!state.success ? (
                  <form onSubmit={handleSubmit} className="grid gap-4">
                    <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-clay-dark">
                      Ich bin …
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {[["pdl", "PDL / WBL"], ["pflegekraft", "Pflegekraft"], ["praxisanl", "Praxisanleitung"], ["traeger", "Träger / Geschäftsführung"]].map(([v, l]) => (
                        <button key={v} type="button"
                          onClick={() => setForm({ ...form, role: v })}
                          data-testid={`campus-role-${v}`}
                          className={`rounded-full px-3 h-10 text-[12px] font-semibold border transition-all ${
                            form.role === v
                              ? "border-terra bg-terra text-white"
                              : "border-cream-300 text-sand-700 hover:border-clay bg-cream-50"
                          }`}
                        >{l}</button>
                      ))}
                    </div>
                    <input required value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Dein Name *" data-testid="campus-name"
                      className="rounded-2xl border border-cream-300 bg-cream-50 px-5 h-13 py-3 text-[15px] outline-none focus:border-terra" />
                    <input required type="email" value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="E-Mail *" data-testid="campus-email"
                      className="rounded-2xl border border-cream-300 bg-cream-50 px-5 h-13 py-3 text-[15px] outline-none focus:border-terra" />
                    <input type="tel" value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="Telefon (optional)" data-testid="campus-phone"
                      className="rounded-2xl border border-cream-300 bg-cream-50 px-5 h-13 py-3 text-[15px] outline-none focus:border-terra" />
                    <Button type="submit" disabled={state.loading}
                      data-testid="campus-submit"
                      className="rounded-full bg-terra hover:bg-terra-dark text-white h-14 text-base font-semibold mt-2">
                      {state.loading ? "Senden …" : (
                        <span className="inline-flex items-center gap-2">
                          <Sparkles className="h-4 w-4" strokeWidth={1.8} />
                          Beratung anfragen
                        </span>
                      )}
                    </Button>
                    <p className="text-[11px] text-sand-500 leading-snug">
                      Wir melden uns innerhalb von 48h persönlich bei dir.
                      <br />Datenschutz gemäß <Link to="/datenschutz" className="underline">Datenschutzerklärung</Link>.
                    </p>
                  </form>
                ) : (
                  <div className="text-center py-6" data-testid="campus-success">
                    <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-brand-rose/30 text-brand-darkred mx-auto">
                      <CheckCircle2 className="h-6 w-6" strokeWidth={1.8} />
                    </div>
                    <h3 className="mt-5 font-display text-2xl text-sand-800">Anfrage angekommen!</h3>
                    <p className="mt-3 text-sand-600 text-[14px] leading-relaxed">
                      Wir melden uns innerhalb von 48 Stunden persönlich bei dir.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* B2B: Unternehmens-Abo */}
        <section id="unternehmen" className="bg-peach-50 py-28 md:py-40">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-6">
                <span className="h-px w-10 bg-terra" />
                <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-clay-dark">
                  Für Träger &amp; Pflegedienste
                </span>
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.75rem] text-sand-800 font-normal tracking-tight leading-[1.1]">
                Unternehmens-Abo: <br />
                <span className="text-terra">Burnout-Schutz fürs Team.</span>
              </h2>
              <p className="mt-7 text-sand-700 text-[15px] leading-relaxed max-w-2xl">
                Wenn deine PDL kündigt oder wegen Burnout ausfällt, kostet dich das fünfstellig.
                Mit dem Dickies-Campus fangen wir deine Führungskräfte auf – fachlich &amp; mental.
                Alle PDLs und Wohnbereichsleitungen deines Trägers bekommen Zugang.
              </p>
            </div>

            <div className="mt-10 grid md:grid-cols-3 gap-5">
              {[
                { t: "Rechtssicherheit", d: "MDK-Vorbereitung, Reform-Updates, Doku-Vorlagen für deinen kompletten Betrieb." },
                { t: "Mitarbeiterbindung", d: "Weiterbildung + Community = weniger Fluktuation, weniger Recruiting-Kosten." },
                { t: "Mentale Soforthilfe", d: "Wenn's brennt, ist deine PDL nicht allein. Wir fangen sie auf." },
              ].map((p) => (
                <div key={p.t} className="rounded-2xl bg-white border border-cream-300 shadow-soft p-6">
                  <h3 className="font-display text-xl text-sand-800 font-semibold leading-tight">{p.t}</h3>
                  <p className="mt-2.5 text-[14px] text-sand-600 leading-relaxed">{p.d}</p>
                </div>
              ))}
            </div>

            <Button asChild data-testid="campus-b2b-cta"
              className="mt-10 rounded-full bg-terra hover:bg-terra-dark text-white h-14 px-8 text-base font-semibold">
              <a href="#anfrage">
                Konditionen anfragen
                <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.8} />
              </a>
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
